import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileText, FlaskConical, ExternalLink, Trash2 } from 'lucide-react';
import { PLATFORM_HELP } from './helpers';

/**
 * Drag-and-drop cookies.txt upload area + secondary actions.
 */
export function CookieDropZone({
    platform,
    isUploading,
    isTesting,
    hasCookies,
    dragOverPlatform,
    onDragOver,
    onDragLeave,
    onDrop,
    onUploadFile,
    onTest,
    onClear,
}) {
    const inputRef = useRef(null);
    const help = PLATFORM_HELP[platform.platform] || {};
    const isDragging = dragOverPlatform === platform.platform;

    const handleSelect = () => inputRef.current?.click();
    const handleChange = (e) => onUploadFile(platform.platform, e.target.files?.[0]);

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); onDragOver(platform.platform); }}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, platform.platform)}
            className={`relative border-2 border-dashed rounded-2xl p-7 sm:p-10 text-center transition-colors ${
                isDragging
                    ? 'border-[#0E2A47] bg-[#0E2A47]/5'
                    : 'border-border bg-secondary/30 hover:border-[#0E2A47]/50'
            }`}
            data-testid={`admin-cookie-dropzone-${platform.platform}`}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".txt,.cookies"
                className="hidden"
                onChange={handleChange}
                data-testid={`admin-cookie-fileinput-${platform.platform}`}
            />
            <Upload className="h-9 w-9 mx-auto text-muted-foreground mb-2" />
            <div className="text-base font-medium">
                Drop your fresh <code className="font-mono">cookies.txt</code> here
            </div>
            <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                {help.note || 'Re-upload anytime your session expires. We auto-filter to only keep cookies for this platform.'}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <Button
                    type="button"
                    onClick={handleSelect}
                    disabled={isUploading}
                    className="bg-[#0E2A47] hover:bg-[#0B223A]"
                    data-testid={`admin-cookie-browse-${platform.platform}-button`}
                >
                    <FileText className="h-4 w-4 mr-1" />
                    {isUploading ? 'Uploading & testing\u2026' : 'Choose cookies.txt'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onTest(platform.platform)}
                    disabled={isTesting || !hasCookies}
                    data-testid={`admin-cookie-test-${platform.platform}-button`}
                >
                    <FlaskConical className="h-4 w-4 mr-1" />
                    {isTesting ? 'Testing\u2026' : 'Re-test connection'}
                </Button>
                {help.login_url && (
                    <a href={help.login_url} target="_blank" rel="noreferrer">
                        <Button type="button" variant="ghost" size="sm">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open {platform.name} portal
                        </Button>
                    </a>
                )}
                {hasCookies && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onClear(platform.platform)}
                        data-testid={`admin-cookie-clear-${platform.platform}-button`}
                        className="text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear cookies
                    </Button>
                )}
            </div>
        </div>
    );
}

export default CookieDropZone;
